import React from 'react';
import type { ObligationSettings, TaxPlan } from '../../types/finances';
import type { ComputedTaxPlan } from '../../utils/taxPlan';
import { formatScenarioMoney } from '../../utils/cashFlowScenario';
import { HoverTip } from '../billing/SupplierBillHover';

interface TaxPlannerModalProps {
  year: number;
  years: number[];
  onYearChange: (year: number) => void;
  plan: TaxPlan;
  settings: ObligationSettings;
  onChange: (plan: TaxPlan) => void;
  onSettingsChange: (settings: ObligationSettings) => void;
  computed: ComputedTaxPlan;
  profitHint: number | null;
  baselineLowest: number | null;
  draftLowest: number | null;
  busy?: boolean;
  error?: string | null;
  onSave: () => void;
}

function pctInput(rate: number): string {
  return String(Math.round(rate * 10000) / 100);
}

function Tip({
  title,
  body,
  children,
  block,
}: {
  title: string;
  body: string;
  children: React.ReactNode;
  block?: boolean;
}) {
  return (
    <HoverTip
      wide
      inModal
      block={block}
      tip={(
        <div className="fin-refresh-tip">
          <strong>{title}</strong>
          <p className="fin-refresh-tip-lead">{body}</p>
        </div>
      )}
    >
      {children}
    </HoverTip>
  );
}

const TaxPlannerModal: React.FC<TaxPlannerModalProps> = ({
  year,
  years,
  onYearChange,
  plan,
  settings,
  onChange,
  onSettingsChange,
  computed,
  profitHint,
  baselineLowest,
  draftLowest,
  busy = false,
  error = null,
  onSave,
}) => {
  const patch = (next: Partial<TaxPlan>) => onChange({ ...plan, ...next });
  const b = computed.breakdown;
  const lowestDelta =
    baselineLowest != null && draftLowest != null ? draftLowest - baselineLowest : null;

  return (
    <div className="fin-obl-embed">
      <Tip
        block
        title="Tax planner"
        body="A cash-planning worksheet for a Tennessee LLC taxed as an S-Corp. It estimates company cash-out for franchise & excise and optional owner federal estimates. It is not a tax return and not CPA advice."
      >
        <p className="fin-modal-help">
          Planning estimate for a Tennessee LLC taxed as an S-Corp — not tax advice or a filing.
        </p>
      </Tip>
      <div className="fin-obl-body fin-obl-body--embedded">
          <div className="fin-obl-toolbar">
            <Tip
              block
              title="Tax year"
              body="Which calendar year these estimates apply to. Switching years loads that year’s saved plan. The forecast only subtracts payment dates that are still in the window."
            >
              <label className="fin-modal-field">
                <span className="fin-modal-label">Tax year</span>
                <select
                  className="fin-modal-select"
                  value={year}
                  onChange={(e) => onYearChange(Number(e.target.value))}
                >
                  {years.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </label>
            </Tip>
            <Tip
              title="Include on live cash line"
              body="When checked, remaining unpaid tax dates subtract from the Forecast cash line. When unchecked, this year stays a sandbox and does not change live cash."
            >
              <label className="fin-modal-toggle">
                <input
                  type="checkbox"
                  checked={plan.enabled}
                  onChange={(e) => patch({ enabled: e.target.checked })}
                />
                Include on live cash line
              </label>
            </Tip>
            <Tip
              title="Distribute cash for owner federal estimates"
              body="An S-Corp pays no entity-level federal income tax. Turn this on if the company will distribute cash so owners can pay quarterly 1040-ES. Turn it off if owners cover federal tax themselves — then federal does not hit company cash."
            >
              <label className="fin-modal-toggle">
                <input
                  type="checkbox"
                  checked={plan.includeOwnerTaxDistributions}
                  onChange={(e) => patch({ includeOwnerTaxDistributions: e.target.checked })}
                />
                Distribute cash for owner federal estimates
              </label>
            </Tip>
          </div>

          <Tip
            block
            title="How this model works"
            body="S-Corp net profit passes through to owners. Regular W-2 is already in that net. Year-end bonuses come off next, then K-1 / QBI / federal estimates and TN franchise & excise. Company cash here is Tennessee franchise & excise plus optional owner distributions for federal estimates."
          >
            <p className="fin-modal-help">
              S-Corp income passes through to owners (no entity-level federal tax). Tennessee has no
              individual wage income tax. The profit number is already net — regular W-2 stays in it.
              Year-end bonuses (gross + employer tax) come off, then K-1. Company cash here is TN
              franchise &amp; excise plus optional owner distributions to cover federal 1040-ES.
            </p>
          </Tip>

          <div className="fin-obl-grid">
            <label className="fin-modal-field">
              <Tip
                title="Estimated net profit"
                body="Year-end net profit — already after regular payroll, COGS, and overhead. The bonus plan is sized from this number, then those bonuses come off before K-1."
              >
                <span className="fin-modal-label">Estimated net profit</span>
              </Tip>
              <Tip
                block
                title="Profit amount"
                body="Type the year-end profit you want to plan against. Bonuses are sized from this number; tax uses what is left after those bonuses."
              >
                <input
                  className="fin-modal-input"
                  type="number"
                  min={0}
                  step={1000}
                  value={plan.estimatedAnnualProfit || ''}
                  onChange={(e) => patch({ estimatedAnnualProfit: Number(e.target.value) || 0 })}
                />
              </Tip>
              {profitHint != null && (
                <Tip
                  title="Use last 12 months of QuickBooks net"
                  body="Fills profit with the sum of the last 12 months of QuickBooks net income from Forecast. It is a trailing actual, not a forecast of this tax year."
                >
                  <button
                    type="button"
                    className="fin-obl-hint"
                    onClick={() => patch({ estimatedAnnualProfit: profitHint })}
                  >
                    Use last 12 mo QB net ({formatScenarioMoney(profitHint)})
                  </button>
                </Tip>
              )}
            </label>
            <Tip
              block
              title="Estimated net worth (franchise)"
              body="Book equity used for Tennessee franchise tax: $0.25 per $100 of net worth, $100 minimum. Leave blank if you only want the $100 minimum once there is any profit. Franchise is skipped entirely when both profit and net worth are $0."
            >
              <label className="fin-modal-field">
                <span className="fin-modal-label">Estimated net worth (franchise)</span>
                <input
                  className="fin-modal-input"
                  type="number"
                  min={0}
                  step={1000}
                  value={plan.estimatedNetWorth || ''}
                  onChange={(e) => patch({ estimatedNetWorth: Number(e.target.value) || 0 })}
                />
              </label>
            </Tip>
            <Tip
              block
              title="Owner federal effective rate"
              body="Blended federal rate applied to the K-1 remainder after QBI. 32 means 32%. This is a planning rate, not a bracket calculator or Form 1040."
            >
              <label className="fin-modal-field">
                <span className="fin-modal-label">Owner federal effective rate %</span>
                <input
                  className="fin-modal-input"
                  type="number"
                  min={0}
                  max={100}
                  step={0.5}
                  value={pctInput(plan.ownerFederalEffectiveRate)}
                  onChange={(e) => patch({ ownerFederalEffectiveRate: (Number(e.target.value) || 0) / 100 })}
                />
              </label>
            </Tip>
            <Tip
              block
              title="Fiscal year-end month"
              body="1–12. December (12) is a calendar year. Tennessee franchise & excise is due the 15th day of the fourth month after year-end — April 15 of the next year when FYE is December."
            >
              <label className="fin-modal-field">
                <span className="fin-modal-label">Fiscal year-end month</span>
                <input
                  className="fin-modal-input"
                  type="number"
                  min={1}
                  max={12}
                  value={settings.fiscalYearEndMonth}
                  onChange={(e) => onSettingsChange({
                    ...settings,
                    fiscalYearEndMonth: Math.min(12, Math.max(1, Number(e.target.value) || 12)),
                  })}
                />
              </label>
            </Tip>
            <Tip
              title="QBI deduction"
              body="Qualified Business Income deduction on the K-1 remainder before the federal rate. Default is 20%. This is a planning switch, not a §199A eligibility test."
            >
              <label className="fin-modal-toggle">
                <input
                  type="checkbox"
                  checked={plan.qbiEnabled}
                  onChange={(e) => patch({ qbiEnabled: e.target.checked })}
                />
                Apply {Math.round(plan.qbiRate * 100)}% QBI deduction
              </label>
            </Tip>
          </div>

          <div className="fin-obl-kpis">
            <Tip
              block
              title="K-1 remainder"
              body="Net profit minus the year-end bonus plan (gross + employer tax), floored at $0. Regular W-2 is already inside the net, so it is not subtracted again. That pass-through is what QBI and the federal rate apply to."
            >
              <div className="fin-kpi-card">
                <span className="fin-kpi-label">K-1 remainder</span>
                <span className="fin-kpi-value">{formatScenarioMoney(b.k1Remainder)}</span>
                <span className="fin-kpi-sub">
                  Bonuses {formatScenarioMoney(b.bonusDeduction)} · QBI {formatScenarioMoney(b.qbiDeduction)} · taxable {formatScenarioMoney(b.taxablePassThrough)}
                </span>
              </div>
            </Tip>
            <Tip
              block
              title="Federal owner estimate"
              body="Taxable K-1 (after QBI) times the owner effective rate. Entity federal is $0 for an S-Corp. Tennessee has no individual wage income tax. This amount hits company cash only if owner distributions are turned on."
            >
              <div className="fin-kpi-card">
                <span className="fin-kpi-label">Federal owner estimate</span>
                <span className="fin-kpi-value">{formatScenarioMoney(b.federalOwnerEstimate)}</span>
                <span className="fin-kpi-sub">Entity federal {formatScenarioMoney(b.federalEntityTax)} · TN income tax {formatScenarioMoney(b.tnPersonalIncomeTax)}</span>
              </div>
            </Tip>
            <Tip
              block
              title="TN franchise & excise"
              body="Excise is 6.5% of the K-1 remainder. Franchise is $0.25 per $100 of net worth, $100 minimum. Together they are the company Tennessee cash-out, usually due April 15 after a calendar year."
            >
              <div className="fin-kpi-card">
                <span className="fin-kpi-label">TN franchise &amp; excise</span>
                <span className="fin-kpi-value">{formatScenarioMoney(b.tnFranchiseAndExcise)}</span>
                <span className="fin-kpi-sub">
                  Excise {formatScenarioMoney(b.tnExcise)} · franchise {formatScenarioMoney(b.tnFranchise)}
                </span>
              </div>
            </Tip>
            <Tip
              block
              title="Company cash out"
              body="Tennessee franchise & excise plus owner federal distributions if that toggle is on. The subtitle compares the lowest projected cash balance against the last saved forecast."
            >
              <div className="fin-kpi-card">
                <span className="fin-kpi-label">Company cash out</span>
                <span className="fin-kpi-value">{formatScenarioMoney(b.companyCashTotal)}</span>
                <span className="fin-kpi-sub">
                  {lowestDelta != null
                    ? `Lowest balance ${lowestDelta < 0 ? '' : '+'}${formatScenarioMoney(lowestDelta)} vs saved`
                    : 'Remaining unpaid dates hit the forecast'}
                </span>
              </div>
            </Tip>
          </div>

          <Tip
            block
            title="Tax payment calendar"
            body="Dated company cash-outs still left in the forecast window. Past months are treated as already paid and hidden. These rows subtract from live cash only when “Include on live cash line” is on."
          >
            <div className="fin-forecast-table-scroll">
              <table className="fin-forecast-table" aria-label="Tax payment calendar">
                <thead>
                  <tr>
                    <th>
                      <Tip title="Payment" body="What this cash-out is: a federal quarterly estimate or Tennessee franchise & excise.">
                        <span>Payment</span>
                      </Tip>
                    </th>
                    <th>
                      <Tip title="Due" body="Calendar date the cash is assumed to leave. Federal estimates use Apr 15, Jun 15, Sep 15, and Jan 15 of the next year.">
                        <span>Due</span>
                      </Tip>
                    </th>
                    <th className="right">
                      <Tip title="Amount" body="Planning dollars for that date. The last federal quarter absorbs rounding so the four quarters add to the owner estimate.">
                        <span>Amount</span>
                      </Tip>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {computed.slices.length === 0 && (
                    <tr>
                      <td colSpan={3}>
                        <Tip
                          title="No remaining payments"
                          body="Either this year is disabled, profit is too low to create tax cash-out, or every dated payment is before this month and is treated as already paid."
                        >
                          <span>No remaining payments in the forecast window.</span>
                        </Tip>
                      </td>
                    </tr>
                  )}
                  {computed.slices.map((slice, idx) => (
                    <tr key={`${slice.label}-${idx}`}>
                      <td>
                        <Tip
                          title={slice.label}
                          body={
                            slice.label.startsWith('Federal')
                              ? 'Owner 1040-ES cash the company is assumed to distribute this quarter. Hidden if owner distributions are off.'
                              : 'Tennessee franchise and excise due the 15th of the fourth month after fiscal year-end.'
                          }
                        >
                          <span>{slice.label}</span>
                        </Tip>
                      </td>
                      <td>
                        <Tip title="Due date" body="This payment is skipped on the live cash line if the date is before the first day of the current month.">
                          <span>
                            {slice.dueDate.toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })}
                          </span>
                        </Tip>
                      </td>
                      <td className="right">
                        <Tip title="Cash out" body="Subtracted from Forecast cash on the due date when this plan is included on the live line.">
                          <span>{formatScenarioMoney(slice.amount)}</span>
                        </Tip>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Tip>
        </div>

      <div className="fin-cockpit-panel-actions">
        {error && (
          <Tip title="Save error" body="The plan did not write to Firestore. Check that you are signed in as an admin and try again.">
            <span className="fin-modal-error">{error}</span>
          </Tip>
        )}
        <Tip title="Save tax plan" body="Writes this year’s profit, rates, and payment calendar. Fiscal year-end is saved with company tax defaults. Admins only.">
          <button type="button" className="fin-primary-btn" onClick={onSave} disabled={busy}>
            Save tax plan
          </button>
        </Tip>
      </div>
    </div>
  );
};

export default TaxPlannerModal;
